<!-- ImprintMethodSelector - Dynamic imprint method selection component -->
<template>
  <div class="imprint-method-selector">
    <div class="section-header">
      <label class="section-label">1. Choose an Imprint Method</label>
      <span v-if="isLocked" class="locked-indicator">(Locked to First Imprint method)</span>
    </div>

    <!-- Calendar Bundle Compatibility Error Message -->
    <p 
      v-if="showBundleCompatibilityError" 
      class="message-calendar-titles"
    >
      Unfortunately, one or more calendar titles in your bundle cannot be ordered together. 
      Please remove one or more titles and try again.
    </p>

    <!-- Method Buttons -->
    <div class="method-buttons">
      <BaseButton
        v-for="method in availableMethods"
        :key="method.sku"
        :text="method.name"
        :variant="getMethodVariant(method.sku)"
        size="small"
        :disabled="isMethodDisabled(method.sku)"
        :class="{ 'hide_tab': isMethodHiddenForBundle(method.sku) }"
        @click="selectMethod(method.sku)"
      />
    </div>

    <!-- Method Description and Image -->
    <div v-if="selectedMethodData && !isMethodHiddenForBundle(selectedMethodSku)" class="method-description">
      <div v-if="selectedMethodData.image" class="method-image">
        <img
          :src="selectedMethodData.image"
          :alt="selectedMethodData.name"
          @error="handleImageError"
        />
      </div>
      <p>{{ selectedMethodData.description }}</p>
    </div>
  </div>
</template>

<script>
import BaseButton from './BaseButton.vue';
import { useImprintStore } from '../stores/useImprintStore';
import { useSelectionStore } from "@/features/imprint/stores/useSelectionStore";
import { useCalendarStore } from '../stores/useCalendarStore';
import { useAppStore } from '../stores/useAppStore';
import { getImprintMethodBySku, getFallbackDescription } from '../static_data/imprintMethodDescriptions';

export default {
  name: 'ImprintMethodSelector',

  components: {
    BaseButton
  },

  props: {
    initialMethod: {
      type: String,
      default: null
    },
    
    // NEW: Disabled prop for locking method selection
    disabled: {
      type: Boolean,
      default: false
    },
    
    // NEW: Imprint index (0 = first, 1 = second, etc.)
    imprintIndex: {
      type: Number,
      default: 0
    }
  },

  setup() {
    const imprintStore = useImprintStore();
    const selectionStore = useSelectionStore();
    const calendarStore = useCalendarStore();
    const appStore = useAppStore();
    
    return {
      imprintStore,
      selectionStore,
      calendarStore,
      appStore
    };
  },

  data() {
    return {
      selectedMethodSku: this.initialMethod
    };
  },

  computed: {
    // Check if bundle compatibility check is required (calendar mode)
    isBundleCheckRequired() {
      return this.appStore.applicationType === 'calendar';
    },
    
    // Show error message when no methods are available for bundle
    showBundleCompatibilityError() {
      return this.isBundleCheckRequired && 
             this.calendarStore.availablePrintMethods.length === 0 &&
             this.calendarStore.hasProducts;
    },
    
    // Check if this selector should be locked (disabled)
    // Only first imprint (index 0) can change method
    isLocked() {
      return this.disabled || this.imprintIndex > 0;
    },
    
    // Get available methods from Pinia store
    availableMethods() {
      return this.imprintStore.availableImprintMethods;
    },

    // Get selected method data
    selectedMethodData() {
      if (!this.selectedMethodSku) return null;
      
      const method = this.availableMethods.find(m => m.sku === this.selectedMethodSku);
      if (!method) return null;

      // Get enhanced data from static descriptions
      const enhancedData = getImprintMethodBySku(this.selectedMethodSku);

      // Safely get image path
      let imagePath = null;
      if (enhancedData?.image) {
        imagePath = this.getImagePath(enhancedData.image);
      } else if (method.image) {
        imagePath = method.image;
      }

      return {
        sku: method.sku,
        name: enhancedData?.name || method.name,
        description: enhancedData?.description || method.description || getFallbackDescription(method.sku),
        image: imagePath // Will be null if no image
      };
    }
  },

  methods: {
    /**
     * Check if method is hidden/disabled for calendar bundle
     */
    isMethodHiddenForBundle(methodSku) {
      if (!this.isBundleCheckRequired) {
        return false;
      }
      return !this.calendarStore.isMethodAvailableForBundle(methodSku);
    },
    
    /**
     * Check if method button should be disabled
     */
    isMethodDisabled(methodSku) {
      if (this.isLocked) {
        return true;
      }
      if (this.isMethodHiddenForBundle(methodSku)) {
        return true;
      }
      return false;
    },
    
    /**
     * Get button variant based on selection and availability
     */
    getMethodVariant(methodSku) {
      if (this.selectedMethodSku === methodSku && !this.isMethodHiddenForBundle(methodSku)) {
        return 'primary';
      }
      return 'secondary';
    },
    
    handleImageError(event) {
      console.warn('[ImprintMethodSelector] Failed to load image:', event.target.src);
      // Hide image container on error
      event.target.style.display = 'none';
    },

    getImagePath(filename) {
      // Import image dynamically from assets
      try {
        return new URL(`../../../assets/imprint-methods/${filename}`, import.meta.url).href;
      } catch (e) {
        console.warn('Failed to load imprint method image:', filename, e);
        return null;
      }
    },

    emitMethodSelectedBySku(sku) {
          const method = this.availableMethods.find(m => m.sku === sku);
          if (!method) return;
          // IMPORTANT: For locked selectors we still must notify parent/store,
          // otherwise methodsData per imprint will not switch and won't restore text/colors.
          this.$emit('method-selected', this.getMethodEmitData(method));
    },

    selectMethod(sku) {
      // VALIDATION: Prevent selection if locked
      if (this.isLocked) {
        console.warn('[ImprintMethodSelector] Method selection is locked for imprint', this.imprintIndex);
        return;
      }
      
      // Prevent selection if method not available for bundle
      if (this.isMethodHiddenForBundle(sku)) {
        console.warn('[ImprintMethodSelector] Method not available for calendar bundle:', sku);
        return;
      }
      
      this.selectedMethodSku = sku;
      const method = this.availableMethods.find(m => m.sku === sku);
      
      if (method) {
        const previousImprintMethod = this.imprintStore.getImprintMethodKey;
        const newImprintMethod = method.name;

        // Update Pinia store (only for first imprint)
        if (this.imprintIndex === 0) {
          this.imprintStore.setSelectedImprintMethod({
            id: this.availableMethods.indexOf(method),
            sku: sku,
            name: method.name,
            data: method
          });

          // Copy data from previous method to new method before cleanup
          if (previousImprintMethod && previousImprintMethod !== newImprintMethod) {
            console.log('[ImprintMethodSelector] HOTFIX: Copying data from', previousImprintMethod, 'to', newImprintMethod);
            this.selectionStore.initImprintKey(newImprintMethod, previousImprintMethod);
          }

          // Remove empty color size lines without data
          this.selectionStore.removeEmptyImprintMethodData(previousImprintMethod);
        }
        
        // Emit to parent
        this.$emit('method-selected', this.getMethodEmitData(method));
      }
    },

    getMethodEmitData(method) {
      return {
        sku: method.sku,
        name: method.name,
        description: this.selectedMethodData?.description || method.description || '',
        image: this.selectedMethodData?.image || method.image || null,
        price: method.price
      };
    },

    initializeSelection() {
      // NEW: For locked selectors (imprints after first), use the method from store
      if (this.isLocked) {
        const storeMethod = this.imprintStore.selectedImprintMethod;
        if (storeMethod && storeMethod.sku) {
          this.selectedMethodSku = storeMethod.sku;
          console.log('[ImprintMethodSelector] Locked to store method:', storeMethod.sku, 'for imprint', this.imprintIndex);
          // CRITICAL: emit so parent can call newImprintStore.setActiveImprintMethod(methodSku, imprintKey)
          // This initializes/switches methodsData for this imprint.
          this.emitMethodSelectedBySku(storeMethod.sku);
          return;
        }
      }
      
      // For calendar bundles - select first AVAILABLE method
      if (this.isBundleCheckRequired) {
        const availableForBundle = this.calendarStore.availablePrintMethods;
        
        if (availableForBundle.length > 0) {
          const firstAvailable = this.availableMethods.find(
            m => availableForBundle.includes(m.sku)
          );
          
          if (firstAvailable) {
            console.log('[ImprintMethodSelector] Calendar bundle: selecting first available method:', firstAvailable.sku);
            this.selectMethod(firstAvailable.sku);
            return;
          }
        } else {
          console.warn('[ImprintMethodSelector] Calendar bundle: no methods available');
          return;
        }
      }
      
      // Auto-select first method if none selected
      if (!this.selectedMethodSku && this.availableMethods.length > 0) {
        this.selectMethod(this.availableMethods[0].sku);
      } else if (this.selectedMethodSku) {
        // Emit initial selection
        const method = this.availableMethods.find(m => m.sku === this.selectedMethodSku);
        if (method) {
          this.selectMethod(this.selectedMethodSku);
        }
      }
    }
  },

  watch: {
    // Watch for changes in available methods
    availableMethods: {
      immediate: true,
      handler(newMethods, oldMethods) {
        console.log('%c[METHOD-SELECTOR] availableMethods watcher fired', 'color: teal; font-weight: bold', {
          methodCount: newMethods?.length,
          selectedMethodSku: this.selectedMethodSku,
          imprintIndex: this.imprintIndex,
          isLocked: this.isLocked,
          oldSkus: oldMethods?.map(m => m.sku),
          newSkus: newMethods?.map(m => m.sku),
        });
        console.trace('[METHOD-SELECTOR] availableMethods watcher trace');
        if (newMethods && newMethods.length > 0) {
          this.initializeSelection();
        }
      }
    },
    
    // Watch for calendar bundle validation changes
    'calendarStore.availablePrintMethods': {
      handler(newAvailableMethods) {
        console.log('[ImprintMethodSelector] Calendar available methods changed:', newAvailableMethods);
        
        if (this.isBundleCheckRequired && 
            this.selectedMethodSku && 
            newAvailableMethods.length > 0 &&
            !newAvailableMethods.includes(this.selectedMethodSku)) {
          
          const firstAvailable = this.availableMethods.find(
            m => newAvailableMethods.includes(m.sku)
          );
          
          if (firstAvailable) {
            console.log('[ImprintMethodSelector] Switching to available method:', firstAvailable.sku);
            this.selectMethod(firstAvailable.sku);
          }
        }
      }
    },
    
    'imprintStore.selectedImprintMethod.sku': {
      handler(newSku) {
        if (this.isLocked && newSku && this.selectedMethodSku !== newSku) {
          this.selectedMethodSku = newSku;
          console.log('[ImprintMethodSelector] Synced locked selector to store method:', newSku, 'for imprint', this.imprintIndex);
          // CRITICAL: also emit so second/third imprints switch their active method data
          this.emitMethodSelectedBySku(newSku);
        }
      }
    }
  },

  mounted() {
    console.log('[ImprintMethodSelector] Mounted', {
      availableMethods: this.availableMethods,
      selectedMethodSku: this.selectedMethodSku,
      imprintIndex: this.imprintIndex,
      isLocked: this.isLocked,
      isBundleCheckRequired: this.isBundleCheckRequired,
      bundleAvailableMethods: this.calendarStore.availablePrintMethods
    });
  }
};
</script>

<style scoped lang="scss">
@use '@/styles/utils' as *;

.imprint-method-selector {
  margin-bottom: 0;
}

.section-header {
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-label {
  display: block;
  font-size: 16px;
  font-weight: 400;
  color: $color-black;
}

.locked-indicator {
  font-size: 12px;
  font-style: italic;
  color: $color-text-light;
  font-weight: 300;
}

// Calendar bundle compatibility error message
.message-calendar-titles {
  color: #E83223;
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 16px;
  padding: 12px 16px;
  background-color: #FFF3F3;
  border: 1px solid #E83223;
  border-radius: 4px;
}

// Method Buttons
.method-buttons {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 48px;
  margin-bottom: 20px;



  :deep(button) {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 10px;
    gap: 10px;
    width: 136.5px;
    height: 44px;
    border-radius: 5px;
    
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      pointer-events: none;
    }
  }
  
  // Hidden tab styling for unavailable calendar bundle methods
  :deep(button.hide_tab) {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }

  :deep(.btn-text) {
    font-weight: 600;
  }
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 30px;

    :deep(button) {
      flex: 1 1 calc(50% - 6px);
      min-width: 0;
    }
  }
  
  @media (max-width: 480px) {
    :deep(button) {
      flex: 1 1 100%;
    }
  }
}

// Method Description
.method-description {
  display: flex;
  gap: 29px;
  align-items: flex-start;
  margin-bottom: 20px;
  text-align: left;
  
  .method-image {
    width: 100px;
    height: 100px;
    flex-shrink: 0;
    display: none;
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 4px;
      box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
    }
  }
  
  p {
    font-size: 16px;
    line-height: 22px;
    color: $color-text;
    margin: 0;
    flex: 1;
    text-align: left;
  }
}

@media (max-width: 768px) {
  .method-buttons {
    flex-direction: column;
    gap:25px;

    button {
      width: 100%;
    }
  }
  
  .method-description {
    flex-direction: column;
    
    .method-image {
      width: 80px;
      height: 80px;
      display: none;
    }
  }
}
</style>
